import {excelFileToRows} from 'common/HelperFunctions'
import ColumnAssignmentModal from 'common/components/dataUpload/ColumnAssignmentModal'
import SheetAssignmentModal from 'common/components/dataUpload/SheetAssignmentModal'
import {
  dispatchApi,
  forgetUploadedFile,
  isAbortError,
  isChunkedUploadAvailable,
  uploadFileInPieces,
} from 'common/helpers/chunkedUpload'
import {
  DATA_UPLOAD_ERROR_KEYS,
  type DataUploadAllocationAssignment,
  type DataUploadAllocationQuestion,
  type DataUploadAssignmentQuestion,
  type DataUploadBranchAssignments,
  type DataUploadBranchQuestion,
  type DataUploadColumnAssignments,
  type DataUploadColumnQuestions,
  type DataUploadCounts,
  type DataUploadSheetAssignments,
  type DataUploadSkippedRow,
  type DataUploadSubjectMapping,
  type DataUploadSubjectMappings,
  MIXED_BRANCH,
  allocationAnswerKey,
  appendAllocationAssignments,
  appendBranchAssignments,
  appendColumnAssignments,
  appendSheetAssignments,
  dataUploadCountsSummary,
  dataUploadErrorCode,
  dataUploadErrorMessage,
  dataUploadSkippedRowKey,
  rejectedSubjectAliasCount,
  skippedAllocationAnswers,
  subjectAliasesBody,
  withConfirmedSubjectsApplied,
  withoutAnsweredAllocations,
  withoutAnsweredBranches,
  withoutAnsweredColumns,
  withoutConfirmedSubjects,
} from 'common/helpers/dataUpload'
import {
  isDataUploadConversionAvailable,
  useDataUploadConfig,
} from 'common/hooks/useDataUploadConfig'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  CloudUpload,
  Download,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Info,
  Loader2,
  X,
} from 'lucide-react'
import React, {FC, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {toast} from 'react-toastify'
import {
  getDataUploadDraft,
  putDataUploadSubjectAliases,
} from 'setup/redux/Actions/DataUpload/dataUpload'
import {extractOnboardingTeachers} from 'setup/redux/Actions/Onboarding/extractOnboardingTeachers'
import {prefillOnboardingFromFile} from 'setup/redux/Actions/Onboarding/prefillOnboardingFromFile'

import {waitForPrefillJob} from '../hooks/useOnboardingPrefillDraft'
import {STEP_TITLE_KEYS} from '../utils/stepTitles'
import {
  TEACHER_FILE_SUMMARY_ON_REVIEW,
  TEACHER_UPLOAD_EXTRACTION_ENABLED,
  TEACHER_UPLOAD_LIMITS,
  TeacherEntryRow,
  TeacherExtraction,
  TeacherFileCounts,
  TeacherFileKind,
  TeacherFileRejection,
  classifyTeacherFile,
  fileExtension,
  formatBytes,
  namesToTeacherRows,
  normalizeExtractionResponse,
  normalizePastedNames,
  parseCsvRows,
  parseTeachersRows,
  splitTeacherNames,
  teacherUploadAccept,
  validateTeacherFiles,
} from '../utils/teacherEntry'

/**
 * "Add Teachers" entry screen of the Teacher Allocation step.
 *
 * Two ways in, one way out: names typed/pasted into the numbered field and files dropped
 * on the upload card are both turned into review rows, merged, and handed to the parent
 * through `onReview` when the user presses Review Teachers. The parent (Step4Manual)
 * appends them to `manualData.teachers` and switches to its review table, where duplicate
 * names and low-confidence extractions are resolved.
 *
 * Files are processed as soon as they are added. On the AI servers every file (spreadsheets
 * too) is uploaded in 512 KB pieces and read by `onboarding/extract-teachers`: a sheet with
 * headers is read without the model in about 2 s, and every row rule stays on the server. The
 * same upload is handed to `onboarding/prefill`, which reads it again in the background for the
 * Students and Teacher Allocation steps. Elsewhere the upload stays Excel-only and the sheet is
 * parsed in the browser, with the same row rules (utils/recordRules.ts).
 */

interface Props {
  isDark: boolean
  /** Teachers already in the list (resume, or coming back from Review to add more). */
  existingCount: number
  showBackButton: boolean
  onBack: () => void
  /**
   * `counts`: the students and allocation lines the files hold for the later steps (the
   * summary's badges), or null when the files were not checked for them. Review shows them as
   * stat cards.
   */
  onReview: (rows: TeacherEntryRow[], counts: TeacherFileCounts | null) => void
  onDownloadTemplate: () => void
  /**
   * Every "which classes?" answer given so far, after each one. The Allocation step fetches the
   * Teachers file's allocations with them, so what was answered here is not asked again.
   */
  onAllocationAnswers?: (answers: DataUploadAllocationAssignment[]) => void
  /**
   * Set to a reader of what Review Teachers would take right now (typed names, files already
   * read), or null when there is nothing. "Save and finish later" adds those rows to the list,
   * since this screen's own state is lost with the page.
   */
  pendingRowsRef?: React.MutableRefObject<
    (() => {rows: TeacherEntryRow[]; counts: TeacherFileCounts | null} | null) | null
  >
}

type UploadStatus =
  | 'parsing'
  | 'uploading'
  | 'extracting'
  | 'ready'
  | 'error'
  /** Waiting for the user to say whose list each sheet of the workbook is. */
  | 'needs-assignment'
  /** Given to another screen on the user's answer: an info line, never an error. */
  | 'assigned'

interface UploadItem {
  id: string
  file: File
  kind: TeacherFileKind
  status: UploadStatus
  rows: TeacherEntryRow[]
  /** 0..1 while `status === 'uploading'`. */
  progress?: number
  /** i18n id of the failure message when `status === 'error'`. */
  errorKey?: string
  /** Already-translated failure copy (a backend code mapped to text); wins over `errorKey`. */
  errorText?: string
  /** What was read: "Read 52 rows. 50 teachers are ready. 1 line is a heading…". */
  summary?: string | null
  /** Rows left out of the file, with the reason. */
  skipped?: DataUploadSkippedRow[]
  /** Set when `status === 'assigned'`: the screen the user gave the file to, or 'ignore'. */
  assignedTo?: string
  /** `file` when the file itself said whose list it is; absent when the user answered. */
  assignedBy?: string
  /**
   * Read, but held no teachers while the request's other files did: a neutral line, not the
   * red "No teacher names were found" (that stays for a request that found nobody).
   */
  noTeachers?: boolean
  /**
   * A file read on the server: how many teachers it gave (`sources[].records`) and how many of
   * them need a look. Its rows are in the one merged list of the request, not on the file.
   */
  found?: number
  needsCheck?: number
}

/** Rows listed under a file before "…and N more". */
const MAX_LISTED_SKIPPED = 50

const useMockExtraction =
  String(import.meta.env.VITE_MOCK_ONBOARDING_TEACHER_EXTRACTION ?? '').toLowerCase() === 'true'

const REJECTION_KEYS: Record<TeacherFileRejection, string> = {
  unsupported: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_UNSUPPORTED',
  'too-large': 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_TOO_LARGE',
  'too-many': 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_TOO_MANY',
  duplicate: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_DUPLICATE',
}

/** `counts` for a sheet the browser parsed, so it gets the same summary as a server read. */
const browserCounts = (kept: number, skipped: DataUploadSkippedRow[]): DataUploadCounts => {
  const leftOut: Record<string, number> = {}
  skipped.forEach((row) => {
    const code = row.code ?? row.reason ?? ''
    if (code) leftOut[code] = (leftOut[code] ?? 0) + 1
  })
  // Nothing the browser parses needs a value filled in, so every kept row is ready.
  return {read: kept + skipped.length, kept, ready: kept, leftOut, needValues: {}}
}

const ERROR_KEYS = {
  read: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_READ',
  noNames: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_NO_NAMES',
  noHeader: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_NO_HEADER',
  extract: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_EXTRACT',
} as const

/** The questions dialog when no branch is asked about: one list, not a new one per render. */
const NO_BRANCH_QUESTIONS: DataUploadBranchQuestion[] = []

let uploadSeq = 0
const nextUploadId = () => `teacher-upload-${Date.now()}-${++uploadSeq}`

/** Fake extraction result for local UI work while the endpoint is being built. */
const buildMockExtraction = (fileName: string) => ({
  files: [
    {
      fileName,
      status: 'ok',
      teachers: [
        {name: 'Emma Wilson', employeeId: 'EMP-101', subject: 'Mathematics', confidence: 0.97},
        {
          name: 'Oliver Bennett',
          employeeId: '',
          subject: 'Physics',
          class: 'Grade 9',
          confidence: 0.91,
        },
        {name: 'Sophie Tumer', employeeId: 'EMP-1O3', confidence: 0.54},
      ],
    },
  ],
})

const FileKindIcon: FC<{kind: TeacherFileKind; size?: number}> = ({kind, size = 18}) => {
  if (kind === 'spreadsheet') return <FileSpreadsheet size={size} aria-hidden />
  if (kind === 'image') return <ImageIcon size={size} aria-hidden />
  return <FileText size={size} aria-hidden />
}

const Step4TeacherEntry: FC<Props> = ({
  isDark,
  existingCount,
  showBackButton,
  onBack,
  onReview,
  onDownloadTemplate,
  onAllocationAnswers,
  pendingRowsRef,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const textareaId = useId()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const gutterRef = useRef<HTMLDivElement>(null)

  const [namesText, setNamesText] = useState('')
  const [files, setFiles] = useState<UploadItem[]>([])
  const [isDragging, setIsDragging] = useState(false)
  /**
   * The one "whose list is this?" question for every file: they are read in one request, so the
   * server asks once, covering each file that needs it (keys `<fileName>::<sheet>`). Closing the
   * dialog without answering leaves the files waiting; their "Answer" link opens it again.
   */
  const [sheetQuestion, setSheetQuestion] = useState<DataUploadAssignmentQuestion | null>(null)
  const [sheetQuestionOpen, setSheetQuestionOpen] = useState(false)
  /**
   * Every sheet answer given so far. The server keeps none: a read without `sheetAssignments`
   * has it ask again, so they go with every read of the files.
   */
  const sheetAnswersRef = useRef<DataUploadSheetAssignments>({})
  /**
   * The questions of the last read, asked in one dialog after its rows appear (they are read
   * either way): "which field is this column?", "which branch are these students in?" and
   * subjects the files write under a name the school does not use. Column answers and confirmed
   * subjects are saved for the school; every answer goes with every later read.
   */
  const [questions, setQuestions] = useState<{
    columns: DataUploadColumnQuestions | null
    branches: DataUploadBranchQuestion[]
    subjects: DataUploadSubjectMappings | null
    /** Teachers whose subjects came with no class: which classes each subject is taught in. */
    allocations?: DataUploadAllocationQuestion[]
    /** Reopened to change earlier branch answers: the cards start from them. */
    initialBranches?: DataUploadBranchAssignments
  } | null>(null)
  /**
   * The branch questions answered so far, so an answer can be changed later ("Both"
   * instead of a branch): the server never asks an answered sheet again.
   */
  const [branchAnswered, setBranchAnswered] = useState<{
    questions: DataUploadBranchQuestion[]
    answers: DataUploadBranchAssignments
  }>({questions: [], answers: {}})
  const [answeringQuestions, setAnsweringQuestions] = useState(false)
  const columnAnswersRef = useRef<DataUploadColumnAssignments>({})
  /**
   * Every branch answer given so far (sheet key -> branch id, or "mixed"). Not saved per school:
   * the branch is a fact about this upload, so they go with every read, like the sheet answers.
   */
  const branchAnswersRef = useRef<DataUploadBranchAssignments>({})
  /**
   * Every teacher + subject answered so far with its classes (empty: not allocated). Sent to
   * `extract-teachers` (it stops asking those) and to `prefill` (it writes one allocation line
   * per picked class), on every read.
   */
  const allocationAnswersRef = useRef<DataUploadAllocationAssignment[]>([])
  /**
   * "Save teachers only" was chosen: their classes are not asked here (nor sent to
   * `extract-teachers` or `prefill`). The Teacher Allocation step asks them instead, from the
   * prefill draft's own `allocationQuestions`.
   */
  const teachersOnlyRef = useRef(false)
  /**
   * Subject mappings confirmed so far, by lower-cased written name. Saved as aliases, so the
   * next read normally reports them as `applied`; a rejected or slow alias can come back as a
   * suggestion, and this keeps it from being asked again.
   */
  const subjectAnswersRef = useRef<Map<string, DataUploadSubjectMapping>>(new Map())
  /**
   * The teachers of the last server read of the files. All of them go in ONE request, so the
   * server merges a teacher named in two files into one row; the list belongs to the request.
   */
  const [serverRows, setServerRows] = useState<TeacherEntryRow[]>([])
  /**
   * What else the files hold, read by `onboarding/prefill` for the later steps: `checking`
   * while its drafts are being read, then how many students / allocations were found.
   */
  const [carried, setCarried] = useState<{
    checking: boolean
    /** Per file (lower-cased name): what that file contributed to each draft. */
    byFile: Record<string, {students: number; allocation: number}>
  } | null>(null)

  // Word / PDF / photo extraction needs the AI server's endpoint (or the local mock).
  const extractionEnabled =
    useMockExtraction || (TEACHER_UPLOAD_EXTRACTION_ENABLED && isDataUploadConversionAvailable())
  // Limits come from `data-upload/use-cases`, never hardcoded; defaults until it answers.
  const uploadConfig = useDataUploadConfig('teachers', extractionEnabled && !useMockExtraction)
  const maxFileBytes = extractionEnabled
    ? uploadConfig.maxFileBytes
    : TEACHER_UPLOAD_LIMITS.maxFileBytes
  /**
   * How many files may be queued, from the same `data-upload/use-cases` limits the server
   * enforces. Without extraction the sheet is parsed in the browser, one at a time.
   *
   * On the AI servers each file is uploaded in pieces, then all of them are read in ONE
   * `extract-teachers` request (`uploadIds`), so the server merges a teacher who is in two files.
   */
  const maxFiles = extractionEnabled ? Math.max(1, uploadConfig.maxFiles) : 1
  const singleFile = maxFiles === 1
  /** Files are uploaded in pieces and read together on the server. */
  const serverRead = extractionEnabled && !useMockExtraction && isChunkedUploadAvailable()

  const names = useMemo(() => splitTeacherNames(namesText), [namesText])
  const lineCount = namesText.length === 0 ? 0 : namesText.split('\n').length
  const fileRowCount =
    files.reduce((sum, f) => sum + (f.status === 'ready' ? f.rows.length : 0), 0) +
    serverRows.length
  const isBusy = files.some(
    (f) => f.status === 'parsing' || f.status === 'uploading' || f.status === 'extracting'
  )
  const hasInput = names.length > 0 || fileRowCount > 0 || existingCount > 0
  // A file waiting to be assigned has been read by nothing yet; reviewing now would silently
  // drop it, so the question is answered (or the file removed) first.
  const awaitingAssignment = files.some((f) => f.status === 'needs-assignment')
  // The files are still being checked for students and allocations: Review waits for the
  // counts, so it never opens with them missing.
  const isChecking = Boolean(carried?.checking)
  const canReview = hasInput && !isBusy && !isChecking && !awaitingAssignment

  // --- Typed / pasted names -------------------------------------------------------

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const normalized = normalizePastedNames(e.clipboardData.getData('text'))
    if (normalized === null) return
    e.preventDefault()
    const el = e.currentTarget
    const start = el.selectionStart
    const end = el.selectionEnd
    const before = namesText.slice(0, start)
    const after = namesText.slice(end)
    // Pasted names always land on their own lines, whatever the caret was touching.
    const prefix = before && !before.endsWith('\n') ? '\n' : ''
    const suffix = after && !after.startsWith('\n') ? '\n' : ''
    const inserted = prefix + normalized + suffix
    setNamesText(before + inserted + after)
    const caret = start + inserted.length
    requestAnimationFrame(() => el.setSelectionRange(caret, caret))
  }

  const syncGutterScroll = () => {
    if (gutterRef.current && textareaRef.current) {
      gutterRef.current.scrollTop = textareaRef.current.scrollTop
    }
  }

  // --- Files ----------------------------------------------------------------------

  const patchFile = useCallback((id: string, patch: Partial<UploadItem>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? {...f, ...patch} : f)))
  }, [])

  /** The server read in flight: a newer read (a file added or removed) stops it. */
  const readAbortRef = useRef<AbortController | null>(null)
  /** Stops watching the prefill drafts when the files change. */
  const prefillAbortRef = useRef<AbortController | null>(null)
  useEffect(
    () => () => {
      readAbortRef.current?.abort()
      prefillAbortRef.current?.abort()
    },
    []
  )

  /**
   * After `onboarding/prefill` answers, wait for its Students and Allocation drafts and say
   * how many rows each holds, so the user knows the files will fill those steps too. Only a
   * count: the steps themselves fetch and apply the drafts when they open.
   */
  const watchCarried = useCallback(
    (prefillResponse: any) => {
      const raw = prefillResponse?.result?.drafts ?? prefillResponse?.drafts ?? {}
      // `{students: {id, job}}`, or the draft id alone.
      const draftOf = (value: any) =>
        typeof value === 'string' || typeof value === 'number'
          ? {id: value}
          : value?.id || value?.draftId
            ? {id: value.id ?? value.draftId, job: value.job}
            : null
      const drafts = {students: draftOf(raw.students), allocation: draftOf(raw.allocation)}
      if (!drafts.students && !drafts.allocation) {
        // Nothing to wait for: the summary can show.
        setCarried(null)
        return
      }
      prefillAbortRef.current?.abort()
      const controller = new AbortController()
      prefillAbortRef.current = controller
      const cancelled = () => controller.signal.aborted
      setCarried({checking: true, byFile: {}})

      /**
       * Rows per file once the job is done. One prefill reads every file, so its job only has
       * the total; the draft's own `files[]` says what each file contributed
       * (`counts.records`). No template call here, which would also re-check allocations
       * against teachers that aren't saved yet.
       */
      const rowsPerFile = async (draft: any): Promise<Map<string, number>> => {
        const perFile = new Map<string, number>()
        if (!draft?.id) return perFile
        const job = await waitForPrefillJob(dispatch, String(draft.id), cancelled)
        if (!job || cancelled()) return perFile
        const full = await dispatchApi(dispatch, (ok, fail) =>
          getDataUploadDraft(String(draft.id), ok, fail)
        ).catch(() => null)
        const body = full?.result ?? full
        const draftFiles: any[] = body?.draft?.files ?? body?.files ?? []
        draftFiles.forEach((file) => {
          const name = String(file?.fileName ?? '')
            .trim()
            .toLowerCase()
          const records = Number(file?.counts?.records ?? 0)
          if (name && records > 0) perFile.set(name, (perFile.get(name) ?? 0) + records)
        })
        return perFile
      }
      Promise.all([rowsPerFile(drafts.students), rowsPerFile(drafts.allocation)]).then(
        ([students, allocation]) => {
          if (cancelled()) return
          const byFile: Record<string, {students: number; allocation: number}> = {}
          new Set([...students.keys(), ...allocation.keys()]).forEach((name) => {
            byFile[name] = {
              students: students.get(name) ?? 0,
              allocation: allocation.get(name) ?? 0,
            }
          })
          setCarried({checking: false, byFile})
        }
      )
    },
    [dispatch]
  )

  /** Summary line under a file: translated from `counts`, else the server's own message. */
  const summarize = useCallback(
    (counts: DataUploadCounts | null, message: string | null) =>
      dataUploadCountsSummary(intl.formatMessage, counts, 'teachers') ?? message,
    [intl]
  )

  /**
   * Copy for a failed file. A known code (the file's `reason`, or `result.code` of a failed
   * call) gets its translated copy, with the count behind it for a row code
   * (`NAME_NOT_ENGLISH`: "4 names are not written in English letters…"). Otherwise the server's
   * `message`; then the call's HTTP status (413, 504, connection lost); then the generic
   * "could not extract".
   */
  const describeFailure = useCallback(
    (item: UploadItem, error: unknown, extraction?: TeacherExtraction): Partial<UploadItem> => {
      const code = extraction?.error ?? dataUploadErrorCode(error)
      const counts = extraction?.counts
      const failed = {
        status: 'error' as const,
        errorKey: ERROR_KEYS.extract,
        skipped: extraction?.skipped ?? [],
        summary: null,
      }
      if (code && code in DATA_UPLOAD_ERROR_KEYS) {
        return {
          ...failed,
          errorText: dataUploadErrorMessage(intl.formatMessage, code, {
            config: uploadConfig,
            useCaseKey: 'teachers',
            fileName: item.file.name,
            count: (code && counts?.leftOut[code]) || counts?.read || undefined,
          }),
        }
      }
      if (extraction?.message) return {...failed, errorText: extraction.message}
      if (!extraction && error !== undefined) {
        return {
          ...failed,
          errorText: dataUploadErrorMessage(intl.formatMessage, error, {
            config: uploadConfig,
            useCaseKey: 'teachers',
            fileName: item.file.name,
          }),
        }
      }
      return {...failed, errorText: undefined}
    },
    [intl, uploadConfig]
  )

  const applyExtraction = useCallback(
    (item: UploadItem, extraction: TeacherExtraction) => {
      // Nothing was read: the workbook holds several lists of people and says of none of them
      // whose it is. Ask, then send the same file again with the answers.
      if (extraction.assignmentQuestion) {
        patchFile(item.id, {status: 'needs-assignment', rows: [], summary: null})
        setSheetQuestion(extraction.assignmentQuestion)
        setSheetQuestionOpen(true)
        return
      }
      if (extraction.assignedTo) {
        // Sent to another step on the user's answer: an info line, never an error. The prefill
        // status under it ("5 students will be filled in on the Students step") still shows.
        patchFile(item.id, {
          status: 'assigned',
          assignedTo: extraction.assignedTo,
          assignedBy: extraction.assignedBy,
          rows: [],
          summary: null,
        })
        return
      }
      if (extraction.error) {
        patchFile(item.id, describeFailure(item, undefined, extraction))
        return
      }
      if (extraction.rows.length === 0) {
        patchFile(item.id, {
          status: 'error',
          errorKey: ERROR_KEYS.noNames,
          errorText: extraction.message ?? undefined,
          skipped: extraction.skipped,
        })
        return
      }
      patchFile(item.id, {
        status: 'ready',
        rows: extraction.rows,
        summary: summarize(extraction.counts, extraction.message),
        skipped: extraction.skipped,
      })
    },
    [describeFailure, patchFile, summarize]
  )

  /**
   * One server read of all the files: the merged teachers, one question for every file that
   * needs one, and a status line per file from `sources[]` (its own `records`, `status` and
   * `assignedTo`).
   */
  const applyServerRead = useCallback(
    (items: UploadItem[], extraction: TeacherExtraction) => {
      if (extraction.assignmentQuestion) {
        // Nothing was read. The files the question is about wait for the answer; the others are
        // read with them once it is given.
        const asked = new Set(
          extraction.assignmentQuestion.sheets.map((sheet) => sheet.fileName ?? '')
        )
        items.forEach((item) =>
          patchFile(
            item.id,
            asked.has('') || asked.has(item.file.name)
              ? {status: 'needs-assignment', rows: [], summary: null}
              : {status: 'extracting'}
          )
        )
        setServerRows([])
        setSheetQuestion(extraction.assignmentQuestion)
        setSheetQuestionOpen(true)
        return
      }
      setServerRows(extraction.rows)
      // Asked now that the rows are read: columns it could not place for sure, sheets whose
      // branch it could not tell, and subjects written under a name the school does not use.
      const columns = withoutAnsweredColumns(extraction.columnQuestions, columnAnswersRef.current)
      const branches = withoutAnsweredBranches(extraction.branchQuestions, branchAnswersRef.current)
      // Mappings confirmed earlier are not asked again; when the dialog opens for something
      // else they show as done.
      const openSubjects = withoutConfirmedSubjects(
        extraction.subjectMappings ?? null,
        new Set(subjectAnswersRef.current.keys())
      )
      const subjects =
        (openSubjects?.suggestions.length ?? 0) > 0
          ? withConfirmedSubjectsApplied(openSubjects, subjectAnswersRef.current)
          : null
      const allocations = teachersOnlyRef.current
        ? []
        : withoutAnsweredAllocations(extraction.allocationQuestions, allocationAnswersRef.current)
      setQuestions(
        columns || branches.length > 0 || subjects || allocations.length > 0
          ? {columns, branches, subjects, allocations}
          : null
      )
      const needsCheckIn = (rows: TeacherEntryRow[]) => rows.filter((r) => r._needsReview).length

      if (items.length === 1) {
        // One file: the request's answer is the file's own, summary and left-out rows included.
        const [item] = items
        const assignedTo = extraction.sources[0]?.assignedTo ?? extraction.assignedTo
        if (assignedTo) {
          const assignedBy = extraction.sources[0]?.assignedTo
            ? extraction.sources[0]?.assignedBy
            : extraction.assignedBy
          patchFile(item.id, {status: 'assigned', assignedTo, assignedBy, rows: [], summary: null})
          return
        }
        if (extraction.error) {
          patchFile(item.id, describeFailure(item, undefined, extraction))
          return
        }
        if (extraction.rows.length === 0) {
          patchFile(item.id, {
            status: 'error',
            errorKey: ERROR_KEYS.noNames,
            errorText: extraction.message ?? undefined,
            skipped: extraction.skipped,
          })
          return
        }
        patchFile(item.id, {
          status: 'ready',
          found: extraction.rows.length,
          needsCheck: needsCheckIn(extraction.rows),
          summary: summarize(extraction.counts, extraction.message),
          skipped: extraction.skipped,
        })
        return
      }

      items.forEach((item, index) => {
        const source =
          extraction.sources.find((entry) => entry.fileName === item.file.name) ??
          (extraction.sources.length === items.length ? extraction.sources[index] : undefined)
        if (source?.assignedTo) {
          patchFile(item.id, {
            status: 'assigned',
            assignedTo: source.assignedTo,
            assignedBy: source.assignedBy,
            summary: null,
          })
          return
        }
        if (source?.status === 'error' || (!source && extraction.error)) {
          patchFile(
            item.id,
            describeFailure(item, undefined, {
              ...extraction,
              error: source?.reason ?? extraction.error ?? 'PARSE_FAILED',
              message: source?.message ?? extraction.message,
              counts: source ? null : extraction.counts,
              skipped: [],
            })
          )
          return
        }
        const found = source?.records ?? source?.rows ?? 0
        // Older servers (and a file that genuinely holds nothing) send no owner: when the other
        // files did give teachers, this one is just empty, not a failure.
        if (found === 0 && source?.status === 'no_relevant_data' && extraction.rows.length > 0) {
          patchFile(item.id, {status: 'ready', found: 0, noTeachers: true, summary: null})
          return
        }
        if (found === 0) {
          patchFile(item.id, {
            status: 'error',
            errorKey: ERROR_KEYS.noNames,
            errorText: source?.message,
          })
          return
        }
        patchFile(item.id, {status: 'ready', found, summary: null})
      })
    },
    [describeFailure, patchFile, summarize]
  )

  /**
   * Upload each file in pieces, as before, then read ALL of them in one `extract-teachers`
   * request (`uploadIds`): the server merges a teacher named in two files into one row, which
   * one request per file could not. The same ids (and answers) go to `onboarding/prefill` once
   * (fire and forget): the Students and Allocation steps pick up what else the files hold.
   *
   * Called with every file on the list whenever it changes, so the merge always covers them all;
   * a read still in flight is stopped. A file already uploaded is not sent again.
   */
  const readOnServer = useCallback(
    async (items: UploadItem[]) => {
      readAbortRef.current?.abort()
      prefillAbortRef.current?.abort()
      setCarried(null)
      setServerRows([])
      setSheetQuestion(null)
      setSheetQuestionOpen(false)
      setQuestions(null)
      if (items.length === 0) {
        readAbortRef.current = null
        return
      }
      const controller = new AbortController()
      readAbortRef.current = controller
      const aborted = () => controller.signal.aborted
      // Every answer so far rides along, since the server remembers none.
      const answers = sheetAnswersRef.current
      const columnAnswers = columnAnswersRef.current
      const branchAnswers = branchAnswersRef.current
      const allocationAnswers = allocationAnswersRef.current
      const upload = (item: UploadItem) =>
        uploadFileInPieces(dispatch, item.file, {
          useCaseKey: 'teachers',
          signal: controller.signal,
          onProgress: (progress) => patchFile(item.id, {progress}),
        })

      items.forEach((item) =>
        patchFile(item.id, {
          status: 'uploading',
          progress: 0,
          rows: [],
          found: undefined,
          needsCheck: undefined,
          assignedTo: undefined,
          summary: null,
          skipped: [],
        })
      )
      const uploaded = await Promise.all(
        items.map(async (item) => {
          try {
            const uploadId = await upload(item)
            if (!aborted()) patchFile(item.id, {status: 'extracting'})
            return {item, uploadId}
          } catch (error) {
            if (!isAbortError(error) && !aborted()) patchFile(item.id, describeFailure(item, error))
            return null
          }
        })
      )
      if (aborted()) return
      const sent = uploaded.filter(
        (entry): entry is {item: UploadItem; uploadId: string} => entry !== null
      )
      if (sent.length === 0) return
      const sentItems = sent.map((entry) => entry.item)

      const post = (uploadIds: string[]) => {
        const form = new FormData()
        form.append('uploadIds', JSON.stringify(uploadIds))
        appendSheetAssignments(form, answers)
        appendColumnAssignments(form, columnAnswers)
        appendBranchAssignments(form, branchAnswers)
        appendAllocationAssignments(form, allocationAnswers)
        return dispatchApi(dispatch, (ok, fail) => extractOnboardingTeachers(form, ok, fail))
      }
      const uploadIds = sent.map((entry) => entry.uploadId)
      // Checking from the moment it is sent, so the summary waits for its counts too.
      setCarried({checking: true, byFile: {}})
      dispatch(
        prefillOnboardingFromFile(
          // The same body as the read, so the Students and Allocation drafts read it the same way.
          {
            uploadIds,
            ...(Object.keys(answers).length > 0 ? {sheetAssignments: answers} : {}),
            ...(Object.keys(columnAnswers).length > 0 ? {columnAssignments: columnAnswers} : {}),
            ...(Object.keys(branchAnswers).length > 0 ? {branchAssignments: branchAnswers} : {}),
            ...(allocationAnswers.length > 0 ? {allocationAssignments: allocationAnswers} : {}),
          },
          watchCarried,
          // A 202 the saga does not count as success still carries the drafts.
          watchCarried
        ) as never
      )
      try {
        let response: unknown
        try {
          response = await post(uploadIds)
        } catch (error) {
          // An expired or half-sent upload is sent again, once, like `withUploadedFile` does.
          const code = dataUploadErrorCode(error)
          if (aborted() || (code !== 'UPLOAD_NOT_FOUND' && code !== 'UPLOAD_INCOMPLETE'))
            throw error
          sentItems.forEach((item) => forgetUploadedFile(item.file))
          response = await post(await Promise.all(sentItems.map(upload)))
        }
        if (aborted()) return
        applyServerRead(
          sentItems,
          normalizeExtractionResponse(
            response,
            sentItems.length === 1 ? sentItems[0].file.name : ''
          )
        )
      } catch (error) {
        if (isAbortError(error) || aborted()) return
        // A failed call can still carry the result (reason, message, counts).
        const extraction = normalizeExtractionResponse(
          error,
          sentItems.length === 1 ? sentItems[0].file.name : ''
        )
        sentItems.forEach((item) =>
          patchFile(
            item.id,
            describeFailure(item, error, extraction.error ? extraction : undefined)
          )
        )
      }
    },
    [applyServerRead, describeFailure, dispatch, patchFile, watchCarried]
  )

  /** Local UI work only (`VITE_MOCK_ONBOARDING_TEACHER_EXTRACTION`): a fake answer, no upload. */
  const extractWithMock = useCallback(
    (item: UploadItem) => {
      patchFile(item.id, {status: 'extracting'})
      window.setTimeout(
        () =>
          applyExtraction(
            item,
            normalizeExtractionResponse(buildMockExtraction(item.file.name), item.file.name)
          ),
        900
      )
    },
    [applyExtraction, patchFile]
  )

  /** Excel-only servers: the sheet is parsed here, with the server's row rules. */
  const parseInBrowser = useCallback(
    async (item: UploadItem) => {
      const ext = fileExtension(item.file.name)
      let sheetRows: string[][]
      try {
        sheetRows =
          ext === 'csv' ? parseCsvRows(await item.file.text()) : await excelFileToRows(item.file)
      } catch {
        patchFile(item.id, {status: 'error', errorKey: ERROR_KEYS.read})
        return false
      }
      const parsed = parseTeachersRows(sheetRows, item.file.name)
      if (parsed.error) return true
      if (parsed.rows.length === 0) {
        // Every row broke a rule: say which one hit most, as the server does in `reason`.
        const {leftOut} = browserCounts(0, parsed.skipped)
        const [topCode, topCount] = Object.entries(leftOut).sort((a, b) => b[1] - a[1])[0] ?? []
        patchFile(item.id, {
          status: 'error',
          errorKey: ERROR_KEYS.noNames,
          skipped: parsed.skipped,
          errorText: topCode
            ? dataUploadErrorMessage(intl.formatMessage, topCode, {count: topCount})
            : undefined,
        })
        return false
      }
      patchFile(item.id, {
        status: 'ready',
        rows: parsed.rows,
        summary:
          parsed.skipped.length > 0
            ? summarize(browserCounts(parsed.rows.length, parsed.skipped), null)
            : null,
        skipped: parsed.skipped,
      })
      return false
    },
    [intl, patchFile, summarize]
  )

  /** A file read in the browser or by the local mock; server files go through `readOnServer`. */
  const processFile = useCallback(
    async (item: UploadItem) => {
      if (item.kind === 'spreadsheet') {
        // `true`: no recognisable header, so the sheet can't be trusted as parsed.
        const unstructured = await parseInBrowser(item)
        if (!unstructured) return
        if (!extractionEnabled) {
          patchFile(item.id, {status: 'error', errorKey: ERROR_KEYS.noHeader})
          return
        }
      }
      extractWithMock(item)
    },
    [extractWithMock, extractionEnabled, parseInBrowser, patchFile]
  )

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return
      // Single-file mode: a new file replaces whatever is queued instead of being refused
      // as "too many", so the user can just drop the corrected sheet again.
      const {accepted, rejected} = validateTeacherFiles(
        singleFile ? [] : files.map((f) => f.file),
        incoming,
        {extractionEnabled, maxFileBytes, maxFiles}
      )

      // One toast per reason, not one per file — a 30-screenshot drop must not spam.
      const reasons = new Set(rejected.map((r) => r.reason))
      reasons.forEach((reason) => {
        if (reason === 'unsupported' && extractionEnabled) {
          // Says which formats work, and asks for a re-save when it is a legacy .xls / .doc.
          const refused = rejected.find((r) => r.reason === 'unsupported')
          toast.error(
            dataUploadErrorMessage(intl.formatMessage, 'FILE_TYPE_NOT_ALLOWED', {
              fileName: refused?.file.name,
            })
          )
          return
        }
        toast.error(
          intl.formatMessage(
            {id: REJECTION_KEYS[reason]},
            {
              limit: reason === 'too-many' ? maxFiles : formatBytes(maxFileBytes),
            }
          )
        )
      })

      const items: UploadItem[] = accepted.map((file) => ({
        id: nextUploadId(),
        file,
        kind: classifyTeacherFile(file, extractionEnabled) ?? 'document',
        status: 'parsing',
        rows: [],
      }))
      if (items.length === 0) return
      const next = singleFile ? items : [...files, ...items]
      setFiles(next)
      if (serverRead) {
        // All of them in one request, so a teacher in two files is merged.
        void readOnServer(next)
        return
      }
      items.forEach((item) => {
        void processFile(item)
      })
    },
    [
      extractionEnabled,
      files,
      intl,
      maxFileBytes,
      maxFiles,
      singleFile,
      serverRead,
      readOnServer,
      processFile,
    ]
  )

  const removeFile = (id: string) => {
    const remaining = files.filter((f) => f.id !== id)
    setFiles(remaining)
    // The merged list came from all of them together: read what is left again.
    if (serverRead) void readOnServer(remaining)
  }

  const openFilePicker = () => fileInputRef.current?.click()

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? [])
    e.target.value = ''
    addFiles(list)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    addFiles(Array.from(e.dataTransfer.files ?? []))
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDragging) setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  // --- Review ---------------------------------------------------------------------

  const reviewRows = () => {
    const fromFiles = files.flatMap((f) => (f.status === 'ready' ? f.rows : []))
    return [...namesToTeacherRows(names), ...fromFiles, ...serverRows]
  }

  const handleReview = () => {
    if (!canReview) return
    onReview(reviewRows(), TEACHER_FILE_SUMMARY_ON_REVIEW ? carriedCounts() : null)
  }

  useEffect(() => {
    if (!pendingRowsRef) return
    pendingRowsRef.current = () => {
      const rows = reviewRows()
      if (rows.length === 0) return null
      return {rows, counts: TEACHER_FILE_SUMMARY_ON_REVIEW ? carriedCounts() : null}
    }
    return () => {
      pendingRowsRef.current = null
    }
  })

  /**
   * The students and allocation lines of all the files, counted as the summary does: an
   * allocation only from a file we found teachers in (an allocation names a teacher). Null when
   * the files were not checked, or the check has not answered.
   */
  const carriedCounts = (): TeacherFileCounts | null => {
    if (!carried || carried.checking) return null
    return files.reduce(
      (sum, item) => {
        const extra = carried.byFile[item.file.name.trim().toLowerCase()]
        if (!extra) return sum
        const teachers = item.status === 'ready' ? (item.found ?? item.rows.length) : 0
        return {
          students: sum.students + extra.students,
          allocation: sum.allocation + (teachers > 0 ? extra.allocation : 0),
        }
      },
      {students: 0, allocation: 0}
    )
  }

  const reviewHintKey =
    isBusy || isChecking
      ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_HINT_BUSY'
      : canReview
        ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_HINT_ENABLED'
        : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_HINT_DISABLED'

  const renderFileStatus = (item: UploadItem) => {
    if (item.status === 'uploading') {
      const percent = Math.round((item.progress ?? 0) * 100)
      return (
        <span className='step4-te-file-status step4-te-file-status--busy'>
          <Loader2 size={14} className='step4-te-spin' aria-hidden />
          {intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_UPLOADING'},
            {percent}
          )}
        </span>
      )
    }
    if (item.status === 'parsing' || item.status === 'extracting') {
      return (
        <span className='step4-te-file-status step4-te-file-status--busy'>
          <Loader2 size={14} className='step4-te-spin' aria-hidden />
          {intl.formatMessage({
            id:
              item.status === 'parsing'
                ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_PARSING'
                : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_EXTRACTING',
          })}
        </span>
      )
    }
    if (item.status === 'error') {
      return (
        <span className='step4-te-file-status step4-te-file-status--error'>
          <AlertTriangle size={14} aria-hidden />
          {item.errorText ?? intl.formatMessage({id: item.errorKey ?? ERROR_KEYS.extract})}
        </span>
      )
    }
    if (item.status === 'needs-assignment') {
      return (
        <span className='step4-te-file-status step4-te-file-status--error'>
          <AlertTriangle size={14} aria-hidden />
          {intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.FILE_STATUS'})}
          <button
            type='button'
            className='btn btn-sm btn-primary ms-2'
            style={{padding: '0.3rem 0.85rem', fontSize: '0.8rem', lineHeight: 1.3}}
            onClick={() => setSheetQuestionOpen(true)}
          >
            {intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.ANSWER'})}
          </button>
        </span>
      )
    }
    // A file that was read (or sent to another step) says what it held in the summary under
    // the list, not in its own box.
    return null
  }

  /**
   * One combined summary under the files: what each file held and where it goes — its teachers
   * here, its students on the Students step, its allocations on the Teacher Allocation step.
   * The later steps' counts come from the one prefill of all the files (`carried.byFile`).
   */
  /**
   * A file with nothing left to do: read, or sent to another step, or (while the sheet
   * question is open) one the question is not about. Shown with a green tick and tint.
   */
  const isSettled = (item: UploadItem) =>
    item.status === 'ready' ||
    item.status === 'assigned' ||
    (item.status === 'extracting' && Boolean(sheetQuestion))

  const renderSummary = () => {
    // Nothing is read until "whose list is this?" is answered; a count from the prefill sent
    // before the answer would only be half the story, so the summary waits for the re-read.
    if (sheetQuestion || awaitingAssignment) return null
    const lines = files.flatMap((item) => {
      const extra = carried?.byFile[item.file.name.trim().toLowerCase()]
      const teachers = item.status === 'ready' ? (item.found ?? item.rows.length) : 0
      const needsCheck =
        item.status === 'ready'
          ? (item.needsCheck ?? item.rows.filter((r) => r._needsReview).length)
          : 0
      // Each thing the file held is a badge, coloured by where it goes.
      const badges: Array<{text: string; tone: string}> = []
      if (teachers > 0) {
        badges.push({
          tone: 'success',
          text: intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_FOUND'},
            {count: teachers}
          ),
        })
      }
      if (needsCheck > 0) {
        badges.push({
          tone: 'warning',
          text: intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_NEEDS_CHECK'},
            {count: needsCheck}
          ),
        })
      }
      if (extra && extra.students > 0) {
        badges.push({
          tone: 'primary',
          text: intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.SUMMARY_STUDENTS'},
            {count: extra.students}
          ),
        })
      }
      // An allocation names a teacher, so a file we found no teacher in cannot have any: the
      // prefill counts a student's class as one, which read as a contradiction.
      if (extra && extra.allocation > 0 && teachers > 0) {
        badges.push({
          tone: 'info',
          text: intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.SUMMARY_ALLOCATION'},
            {count: extra.allocation}
          ),
        })
      }
      if (item.status === 'assigned' && item.assignedTo === 'ignore') {
        badges.push({
          tone: 'secondary',
          text: intl.formatMessage({
            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_NOT_READ',
          }),
        })
      } else if (item.status === 'assigned' && !extra?.students && !extra?.allocation) {
        // Given to another step, before its count is known. The file itself said whose list it
        // is ("moved"), or the user answered the sheet question ("sent").
        const step = STEP_TITLE_KEYS[item.assignedTo ?? '']
          ? intl.formatMessage({id: STEP_TITLE_KEYS[item.assignedTo!]})
          : item.assignedTo
        badges.push({
          tone: 'primary',
          text: intl.formatMessage(
            {
              id:
                item.assignedBy === 'file'
                  ? 'DATA_UPLOAD.CONVERT.MOVED_TO_STEP'
                  : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_SENT_TO_STEP',
            },
            {step}
          ),
        })
      } else if (item.status === 'ready' && item.noTeachers) {
        badges.push({
          tone: 'secondary',
          text: intl.formatMessage({
            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_NO_TEACHERS',
          }),
        })
      }
      return badges.length > 0 ? [{item, badges}] : []
    })
    if (lines.length === 0) return null
    return (
      <section
        className='mb-4'
        style={{
          border: '1px solid var(--s4te-card-border)',
          borderRadius: 12,
          background: 'var(--s4te-card-bg)',
          padding: '0.9rem 1rem 0.35rem',
        }}
        aria-labelledby={`${textareaId}-summary-title`}
      >
        <h4
          id={`${textareaId}-summary-title`}
          className='d-flex align-items-center gap-2 mb-2'
          style={{fontSize: '0.92rem', fontWeight: 700, color: 'var(--s4te-text)'}}
        >
          <CheckCircle2 size={16} style={{color: 'var(--s4te-success)'}} aria-hidden />
          {intl.formatMessage({
            id:
              files.length > 1
                ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.SUMMARY_TITLE_FILES'
                : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.SUMMARY_TITLE',
          })}
        </h4>
        <ul className='list-unstyled mb-0'>
          {lines.map(({item, badges}, index) => (
            <li
              key={item.id}
              className='d-flex flex-wrap align-items-center justify-content-between gap-2 py-2'
              style={index > 0 ? {borderTop: '1px solid var(--s4te-card-border)'} : undefined}
            >
              <span className='d-flex align-items-center gap-2' style={{minWidth: 0}}>
                <span className='step4-te-file-icon' style={{flex: '0 0 auto'}}>
                  <FileKindIcon kind={item.kind} size={16} />
                </span>
                <span style={{minWidth: 0}}>
                  <span
                    className='d-block text-truncate fw-semibold'
                    style={{fontSize: '0.86rem', color: 'var(--s4te-text)'}}
                    title={item.file.name}
                  >
                    {item.file.name}
                  </span>
                  {item.summary ? (
                    <span
                      className='d-block'
                      style={{fontSize: '0.76rem', color: 'var(--s4te-muted)'}}
                    >
                      {item.summary}
                    </span>
                  ) : null}
                </span>
              </span>
              <span className='d-flex flex-wrap justify-content-end gap-2'>
                {badges.map((badge) => (
                  <span
                    key={badge.text}
                    className={`badge badge-light-${badge.tone} fw-semibold`}
                    style={{fontSize: '0.74rem', padding: '0.35rem 0.6rem'}}
                  >
                    {badge.text}
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      </section>
    )
  }

  /**
   * One loader for all the files of a server read, under the list: uploading (all files'
   * progress together), then reading, then checking what the later steps get. The summary shows
   * only once all of it has answered. Null when nothing is pending.
   */
  const renderLoader = () => {
    if (!serverRead) return null
    const uploading = files.filter((f) => f.status === 'uploading')
    const pending = files.filter((f) => f.status === 'uploading' || f.status === 'extracting')
    // Files waiting on the sheet question are not being read.
    const reading = !sheetQuestion && files.some((f) => f.status === 'extracting')
    const line = (text: string, hint?: string, percent?: number) => (
      <div className='step4-te-file-body mb-3'>
        <span className='step4-te-file-status step4-te-file-status--busy'>
          <Loader2 size={14} className='step4-te-spin' aria-hidden />
          {text}
        </span>
        {percent !== undefined && (
          <span
            className='step4-te-file-progress'
            role='progressbar'
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
          >
            <span style={{width: `${percent}%`}} />
          </span>
        )}
        {hint && <span className='step4-te-file-hint'>{hint}</span>}
      </div>
    )
    if (uploading.length > 0) {
      const done = pending.reduce(
        (sum, f) => sum + (f.status === 'uploading' ? (f.progress ?? 0) : 1),
        0
      )
      const percent = Math.round((done / pending.length) * 100)
      return line(
        intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_UPLOADING'},
          {percent}
        ),
        undefined,
        percent
      )
    }
    if (reading) {
      return line(
        intl.formatMessage({
          id:
            files.length > 1
              ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_EXTRACTING_FILES'
              : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_EXTRACTING',
        }),
        intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE_SLOW'})
      )
    }
    if (carried?.checking && !sheetQuestion) {
      return line(
        intl.formatMessage({
          id:
            files.length > 1
              ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.CARRIED_CHECKING_FILES'
              : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.CARRIED_CHECKING',
        })
      )
    }
    return null
  }

  /**
   * The branch each sheet was put in, with a way to change it: the server never asks an answered
   * sheet again, and a changed answer replaces the branch the old one set.
   */
  const renderBranchAnswers = () => {
    const {answers} = branchAnswered
    const answered = branchAnswered.questions.filter((question) => question.key in answers)
    if (answered.length === 0) return null
    const lines = answered.map((question) => {
      const answer = answers[question.key]
      const branch =
        answer === MIXED_BRANCH
          ? intl.formatMessage({id: 'DATA_UPLOAD.BRANCHES.MIXED'})
          : (question.branches.find((entry) => entry.branchId === answer)?.branch ?? String(answer))
      return `${question.sheet || question.fileName || question.key} → ${branch}`
    })
    return (
      <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3 fs-7'>
        <span className='d-inline-flex align-items-center gap-2 text-muted' style={{minWidth: 0}}>
          <Info size={15} aria-hidden style={{flexShrink: 0}} />
          <span className='text-break'>
            {intl.formatMessage({id: 'DATA_UPLOAD.BRANCHES.ANSWERED'}, {sheets: lines.join(' · ')})}
          </span>
        </span>
        <button
          type='button'
          className='btn btn-sm btn-light-primary'
          onClick={() =>
            setQuestions({
              columns: null,
              branches: answered,
              subjects: null,
              initialBranches: answers,
            })
          }
        >
          {intl.formatMessage({id: 'DATA_UPLOAD.BRANCHES.CHANGE'})}
        </button>
      </div>
    )
  }

  /** The rows a file left out, and why: collapsed under the file like the Students report. */
  const renderSkipped = (item: UploadItem) => {
    const skipped = item.skipped ?? []
    if (skipped.length === 0 || (item.status !== 'ready' && item.status !== 'error')) return null
    const unnamed = intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.UNNAMED_ROW'})
    return (
      <details className='step4-te-file-skipped'>
        <summary>
          {intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.LEFT_OUT'},
            {count: skipped.length}
          )}
        </summary>
        <ul>
          {skipped.slice(0, MAX_LISTED_SKIPPED).map((row, index) => {
            const key = dataUploadSkippedRowKey(row)
            const detail = key ? intl.formatMessage({id: key}) : row.message || row.reason
            return (
              <li key={row.recordId ?? `${row.label}-${index}`}>
                <strong>{row.label || unnamed}</strong>
                {detail ? <span> · {detail}</span> : null}
              </li>
            )
          })}
          {skipped.length > MAX_LISTED_SKIPPED && (
            <li className='step4-te-file-skipped-more'>
              {intl.formatMessage(
                {id: 'DATA_UPLOAD.CONVERT.MORE_ROWS'},
                {count: skipped.length - MAX_LISTED_SKIPPED}
              )}
            </li>
          )}
        </ul>
      </details>
    )
  }

  return (
    <div className={`step4-teacher-entry${isDark ? ' step4-teacher-entry--dark' : ''}`}>
      <div className='step4-te-header'>
        <h2 className='step4-te-title'>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.HEADING'})}
        </h2>
        <p className='step4-te-subtitle'>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.SUBHEADING'})}
        </p>
        {existingCount > 0 && (
          <p className='step4-te-existing'>
            <Info size={14} aria-hidden />
            {intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.EXISTING_COUNT'},
              {count: existingCount}
            )}
          </p>
        )}
      </div>

      <div className='step4-te-grid'>
        {/* --- Add / paste names ------------------------------------------------ */}
        <section className='step4-te-card' aria-labelledby={`${textareaId}-title`}>
          <div className='step4-te-card-head'>
            <span className='step4-te-card-icon step4-te-card-icon--paste'>
              <ClipboardList size={22} aria-hidden />
            </span>
            <div className='step4-te-card-head-text'>
              <div className='step4-te-card-title-row'>
                <h3 id={`${textareaId}-title`} className='step4-te-card-title'>
                  {intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_TITLE',
                  })}
                </h3>
                <span className='step4-te-badge'>
                  {intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.RECOMMENDED',
                  })}
                </span>
              </div>
              <p className='step4-te-card-desc'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_DESCRIPTION',
                })}
              </p>
            </div>
          </div>

          <div className={`step4-te-lines${lineCount === 0 ? ' step4-te-lines--empty' : ''}`}>
            {lineCount > 0 && (
              <div className='step4-te-gutter' ref={gutterRef} aria-hidden>
                {Array.from({length: lineCount}, (_, i) => (
                  <div key={i} className='step4-te-gutter-line'>
                    {i + 1}
                  </div>
                ))}
              </div>
            )}
            <textarea
              id={textareaId}
              ref={textareaRef}
              className='step4-te-textarea'
              wrap='off'
              spellCheck={false}
              autoComplete='off'
              value={namesText}
              placeholder={intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_PLACEHOLDER',
              })}
              onChange={(e) => setNamesText(e.target.value)}
              onPaste={handlePaste}
              onScroll={syncGutterScroll}
            />
          </div>

          <div className='step4-te-help'>
            {names.length > 0 && (
              <p className='step4-te-help-count'>
                {intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_COUNT'},
                  {count: names.length}
                )}
              </p>
            )}
            <p>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_HELP_SEPARATORS',
              })}
            </p>
            <p>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_HELP_DUPLICATES',
              })}
            </p>
          </div>
        </section>

        {/* --- Upload ------------------------------------------------------------ */}
        <section className='step4-te-card' aria-labelledby={`${textareaId}-upload-title`}>
          <div className='step4-te-card-head'>
            <span className='step4-te-card-icon step4-te-card-icon--upload'>
              <FileSpreadsheet size={22} aria-hidden />
            </span>
            <div className='step4-te-card-head-text'>
              <h3 id={`${textareaId}-upload-title`} className='step4-te-card-title'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_TITLE',
                })}
              </h3>
              <p className='step4-te-card-desc'>
                {intl.formatMessage({
                  id: extractionEnabled
                    ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_DESCRIPTION_ANY'
                    : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_DESCRIPTION',
                })}
              </p>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type='file'
            multiple={!singleFile}
            accept={teacherUploadAccept(extractionEnabled)}
            className='d-none'
            onChange={handleInputChange}
          />

          <div
            role='button'
            tabIndex={0}
            className={`step4-te-dropzone${isDragging ? ' step4-te-dropzone--active' : ''}`}
            onClick={openFilePicker}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openFilePicker()
              }
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <span className='step4-te-dropzone-icon'>
              <CloudUpload size={26} aria-hidden />
            </span>
            <p className='step4-te-dropzone-title'>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.DROPZONE_TITLE',
              })}
            </p>
            <p className='step4-te-dropzone-formats'>
              {intl.formatMessage({
                id: extractionEnabled
                  ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.DROPZONE_FORMATS_ANY'
                  : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.DROPZONE_FORMATS',
              })}
            </p>
            <p className='step4-te-dropzone-limits'>
              {intl.formatMessage(
                {
                  id: extractionEnabled
                    ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_LIMITS_ANY'
                    : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_LIMITS',
                },
                {
                  files: maxFiles,
                  perFile: formatBytes(maxFileBytes),
                  total: formatBytes(maxFileBytes * maxFiles),
                }
              )}
            </p>
            <button
              type='button'
              className='step4-te-choose-btn'
              onClick={(e) => {
                e.stopPropagation()
                openFilePicker()
              }}
            >
              <CloudUpload size={16} aria-hidden />
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.CHOOSE_FILES',
              })}
            </button>
            <button
              type='button'
              className='step4-te-template-link'
              onClick={(e) => {
                e.stopPropagation()
                onDownloadTemplate()
              }}
            >
              <Download size={14} aria-hidden />
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.DOWNLOAD_TEMPLATE',
              })}
            </button>
          </div>

          {files.length > 0 && (
            <ul className='step4-te-files' aria-live='polite'>
              {files.map((item) => (
                <li
                  key={item.id}
                  className={`step4-te-file step4-te-file--${item.status}`}
                  style={
                    isSettled(item)
                      ? {
                          background: 'rgba(80, 205, 137, 0.08)',
                          borderColor: 'rgba(80, 205, 137, 0.45)',
                        }
                      : undefined
                  }
                >
                  <span className='step4-te-file-icon'>
                    <FileKindIcon kind={item.kind} />
                  </span>
                  <span className='step4-te-file-body'>
                    <span className='d-flex align-items-center gap-2' style={{minWidth: 0}}>
                      <span className='step4-te-file-name' title={item.file.name}>
                        {item.file.name}
                      </span>
                      {isSettled(item) && (
                        <CheckCircle2
                          size={16}
                          style={{color: 'var(--s4te-success)', flex: '0 0 auto'}}
                          aria-label={intl.formatMessage({
                            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_SETTLED',
                          })}
                        />
                      )}
                    </span>
                    <span className='step4-te-file-meta'>
                      <span className='step4-te-file-size'>{formatBytes(item.file.size)}</span>
                      {(serverRead
                        ? item.status === 'error' || item.status === 'needs-assignment'
                        : item.status !== 'ready' && item.status !== 'assigned') && (
                        <>
                          {' · '}
                          {renderFileStatus(item)}
                        </>
                      )}
                    </span>
                    {/* Server reads show one loader for all the files, under the list. */}
                    {!serverRead && item.status === 'uploading' && (
                      <span
                        className='step4-te-file-progress'
                        role='progressbar'
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round((item.progress ?? 0) * 100)}
                      >
                        <span style={{width: `${Math.round((item.progress ?? 0) * 100)}%`}} />
                      </span>
                    )}
                    {!serverRead && item.status === 'extracting' && (
                      <span className='step4-te-file-hint'>
                        {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE_SLOW'})}
                      </span>
                    )}
                    {renderSkipped(item)}
                  </span>
                  <button
                    type='button'
                    className='step4-te-file-remove'
                    onClick={() => removeFile(item.id)}
                    aria-label={intl.formatMessage({
                      id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REMOVE_FILE',
                    })}
                    title={intl.formatMessage({
                      id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REMOVE_FILE',
                    })}
                  >
                    <X size={16} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {files.length > 0 &&
            (renderLoader() ?? (TEACHER_FILE_SUMMARY_ON_REVIEW ? null : renderSummary()))}
          {files.length > 0 && !isBusy && renderBranchAnswers()}

          <div className='step4-te-note'>
            <span className='step4-te-note-icon'>
              <Info size={18} aria-hidden />
            </span>
            <ul className='step4-te-note-list'>
              <li>
                {intl.formatMessage({
                  id: extractionEnabled
                    ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_NOTE_EXTRACT_ANY'
                    : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_NOTE_EXTRACT',
                })}
              </li>
              {extractionEnabled && (
                <>
                  <li>{intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE_SLOW'})}</li>
                  <li>{intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.PHOTO_TIPS'})}</li>
                </>
              )}
              <li>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_NOTE_REVIEW',
                })}
              </li>
            </ul>
          </div>
        </section>
      </div>

      <div className='step4-te-footer'>
        {showBackButton && (
          <button type='button' className='onboarding-btn-back' onClick={onBack}>
            <ArrowLeft size={16} className='me-2' />
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
          </button>
        )}
        <div className='step4-te-footer-cta'>
          <button
            type='button'
            className='onboarding-btn-next'
            disabled={!canReview}
            onClick={handleReview}
          >
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_BTN'})}
            <ArrowRight size={16} className='ms-2' />
          </button>
          <span className='step4-te-footer-hint'>{intl.formatMessage({id: reviewHintKey})}</span>
        </div>
      </div>

      {/* One dialog for every file (they are read in one request). Closing without answering
          leaves the files waiting: their status line offers the question again, so a workbook
          is never read as the wrong screen's list by default. */}
      <SheetAssignmentModal
        show={sheetQuestionOpen && Boolean(sheetQuestion)}
        question={sheetQuestion}
        files={files.map((item) => item.file)}
        onCancel={() => setSheetQuestionOpen(false)}
        onConfirm={(assignments) => {
          setSheetQuestionOpen(false)
          // Kept for every later read: the server asks the same question again without them.
          sheetAnswersRef.current = {...sheetAnswersRef.current, ...assignments}
          void readOnServer(files)
        }}
      />

      {/* After the rows appear, in one dialog: "which field is this column?", "which branch
          are these students in?" and "Maths -> Mathematics". Confirm saves the ticked subjects
          for the school, then reads the files again ONCE with every answer; Skip keeps the rows
          as they are. */}
      <ColumnAssignmentModal
        show={Boolean(questions)}
        columns={questions?.columns ?? null}
        branches={questions?.branches ?? NO_BRANCH_QUESTIONS}
        initialBranches={questions?.initialBranches}
        subjects={questions?.subjects ?? null}
        allocations={questions?.allocations}
        // Subjects with no class: save the teachers only (the default), or pick classes now.
        peopleOnly={(questions?.allocations?.length ?? 0) > 0 ? 'teachers' : undefined}
        files={files.map((item) => item.file)}
        submitting={answeringQuestions}
        onSkip={() => {
          const skipped = skippedAllocationAnswers(questions?.allocations)
          setQuestions(null)
          if (skipped.length === 0) return
          // "Which classes?" skipped: those subjects are not allocated (answered with no class,
          // for extract-teachers and the prefill alike), and the files are read once more, so
          // the Teacher Allocation step does not ask them again either.
          const skippedKeys = new Set(skipped.map(allocationAnswerKey))
          allocationAnswersRef.current = [
            ...allocationAnswersRef.current.filter(
              (answer) => !skippedKeys.has(allocationAnswerKey(answer))
            ),
            ...skipped,
          ]
          onAllocationAnswers?.(allocationAnswersRef.current)
          void readOnServer(files)
        }}
        onConfirm={(answers) => {
          columnAnswersRef.current = {...columnAnswersRef.current, ...answers.columns}
          answers.subjects.forEach((mapping) =>
            subjectAnswersRef.current.set(mapping.written.toLowerCase(), mapping)
          )
          branchAnswersRef.current = {...branchAnswersRef.current, ...answers.branches}
          /** Classes were sent before: the files are read again without them. */
          let droppedClasses = false
          if (answers.peopleOnly) {
            // No class answers go anywhere; the Allocation step asks them itself.
            teachersOnlyRef.current = true
            droppedClasses = allocationAnswersRef.current.length > 0
            allocationAnswersRef.current = []
          } else {
            if ((questions?.allocations?.length ?? 0) > 0) teachersOnlyRef.current = false
            // A re-answered teacher + subject replaces its earlier answer.
            const answeredNow = new Set(answers.allocations.map(allocationAnswerKey))
            allocationAnswersRef.current = [
              ...allocationAnswersRef.current.filter(
                (answer) => !answeredNow.has(allocationAnswerKey(answer))
              ),
              ...answers.allocations,
            ]
          }
          onAllocationAnswers?.(allocationAnswersRef.current)
          if (questions && Object.keys(answers.branches).length > 0) {
            // Kept so an answer can be changed later; a re-answered sheet replaces its entry.
            const asked = new Set(questions.branches.map((question) => question.key))
            setBranchAnswered((prev) => ({
              questions: [
                ...prev.questions.filter((question) => !asked.has(question.key)),
                ...questions.branches,
              ],
              answers: {...prev.answers, ...answers.branches},
            }))
          }
          const reread = () => {
            setAnsweringQuestions(false)
            setQuestions(null)
            void readOnServer(files)
          }
          // "Save teachers only" and nothing else answered: the rows already read stand.
          if (
            answers.peopleOnly &&
            !droppedClasses &&
            Object.keys(answers.columns).length === 0 &&
            Object.keys(answers.branches).length === 0 &&
            answers.subjects.length === 0
          ) {
            setQuestions(null)
            return
          }
          if (answers.subjects.length === 0) {
            reread()
            return
          }
          setAnsweringQuestions(true)
          dispatchApi(dispatch, (ok, fail) =>
            putDataUploadSubjectAliases(subjectAliasesBody(answers.subjects), ok, fail)
          )
            .then((res) => {
              const rejected = rejectedSubjectAliasCount(res)
              if (rejected > 0) {
                toast.warning(
                  intl.formatMessage({id: 'DATA_UPLOAD.SUBJECTS.REJECTED'}, {count: rejected})
                )
              }
              reread()
            })
            .catch((error) => {
              setAnsweringQuestions(false)
              toast.error(
                dataUploadErrorMessage(intl.formatMessage, error, {
                  config: uploadConfig,
                  useCaseKey: 'teachers',
                })
              )
            })
        }}
      />
    </div>
  )
}

export default Step4TeacherEntry
